<script context="module">
  // ---- Färgöversättningstabell ----
  // svgArticle har ett eget litet SVG-bibliotek (den här filen) i stället
  // för att dela scripts/transformSvgToDarkMode.js - så tabellen och
  // text-färgbytesfunktionen (för introduction/subtext) hör hemma här,
  // inte i den delade filen.
  export const darkModeConversionTable = {
    // text color
    "#050505": "#EDEDED",
    "#0D0D0D": "#EDEDED",
    "#FFF": "#171717",
    "#FFFFFF": "#171717",
    // true black to true white
    "#000": "#FFFFFF",
    "#000000": "#FFFFFF",
    "#010101": "#FFFFFF",
    // graph marking and line color
    "#666666": "#B8B8B8",
    "#666": "#B8B8B8",
    // red-palette
    "#F99999": "#980806",
    "#DA000D": "#F43434",
    "#980806": "#F99999",
    "#EF7171": "#980806",
    // green-palette
    "#BFEDDC": "#167057",
    "#60bCA1": "#60bCA1",
    "#167057": "#BFEDDC",
    // pink-palette
    "#FFCFE4": "#9F0A47",
    "#FF589B": "#FF589B",
    "#9F0A47": "#FFCFE4",
    // yellow-palette
    "#FDC226": "#CC5A05",
    "#F58D2D": "#F58D2D",
    "#CC5A05": "#FDC226",
    // blue-palette
    "#C1DCEE": "#173D56",
    "#568CBB": "#568CBB",
    "#173D56": "#C1DCEE",
    // nya kulturblå
    "#227AC3": "#227AC3",
    // grey-scale-palette
    "#F2F2F2": "#4D4D4D",
    "#D9D9D9": "#737373",
    "#A6A6A6": "#A6A6A6",
    "#737373": "#D9D9D9",
    "#4D4D4D": "#F2F2F2",
  };

  /*
   * Introduction/subtext kan innehålla fri HTML (legend med färgade
   * pluppar, se DN-Plupp/DN-Plupp-Rits) där färgen sätts som en hex-kod
   * i style-attributet (t.ex. style='--plupp-färg:#fa0715;'). Dessa
   * hex-koder byts ut mot samma tabell som SVG:erna, så de switchar
   * färg precis som övrig grafik.
   */
  const HEX_COLOR_REGEX = /#[0-9a-fA-F]{3,8}/g;
  const STYLE_ATTR_REGEX = /style\s*=\s*(['"])([\s\S]*?)\1/gi;

  const replaceHexColorsInStyle = (styleContent) =>
    styleContent.replace(HEX_COLOR_REGEX, (hex) => {
      const mapped = darkModeConversionTable[hex.toUpperCase()];
      return mapped ?? hex;
    });

  export const transformHtmlColorsToDarkMode = (html, darkMode) => {
    if (!darkMode || typeof html !== "string" || !html) return html;

    return html.replace(
      STYLE_ATTR_REGEX,
      (match, quote, styleContent) =>
        `style=${quote}${replaceHexColorsInStyle(styleContent)}${quote}`
    );
  };
</script>

<script>
  import { onMount, afterUpdate } from "svelte";
  import { doPostRatio } from "../../../scripts/helpers.js";

  export let src = "";
  export let alt = "";
  export let className = "";
  export let style = "";
  export let darkMode = false; // kommer utifrån huvudkomponenten
  export let aniPop = true;

  let svgMarkup = "";
  let wrapper; // referens till wrapper-div

  let show = true;

  // ---- Dark mode + special-id ----
  function transformToDarkMode(svgDoc) {
    const all = svgDoc.querySelectorAll("*");

    all.forEach((el) => {
      const id = el.getAttribute("id")?.toLowerCase() || "";

      // 🧩 "bevara" → hoppa över
      if (id.includes("bevara")) return;

      // 🎨 "översätt:_x23_XXXXXX" → sätt explicit färg
      if (id.includes("översätt:")) {
        const hexValue = id.split(":")[1]?.replace("_x23_", "#");
        if (hexValue) {
          ["fill", "stroke"].forEach((attr) => {
            const color = el.getAttribute(attr);
            if (color) el.setAttribute(attr, hexValue);
          });
        }
        return;
      }

      // 🌙 vanlig färgkonvertering
      ["fill", "stroke"].forEach((attr) => {
        const color = el.getAttribute(attr);
        if (color && darkModeConversionTable[color.toUpperCase()]) {
          el.setAttribute(attr, darkModeConversionTable[color.toUpperCase()]);
        }
      });
    });
  }

  // ---- Ladda SVG ----
  onMount(async () => {
    if (!src) return;
    const res = await fetch(src);
    const svgText = await res.text();

    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, "image/svg+xml");
    const svg = doc.documentElement;

    // 🪄 Lägg till renderingsegenskaper automatiskt
    applyRenderingAttributes(svg);

    if (darkMode) transformToDarkMode(svg);

    // Serialisera för rendering
    svgMarkup = new XMLSerializer().serializeToString(svg);

  });

  // ---- Init interaktivitet efter render ----
afterUpdate(() => {

requestAnimationFrame(() => {
  const svg = wrapper?.querySelector("svg");
  if (!svg || svg.dataset.interactiveReady) return;

  // 🧩 Hitta lager (stöd för id, data-name, inkscape:label)
    const targets = findLayer(svg, "mytargets");
    const popups = findLayer(svg, "mypopups");
    const startmess = findLayer(svg, "startmess");

  if (!targets || !popups) {
    // console.warn("Inga interaktiva lager hittades i SVG.");
    return;
  }

  // 🔧 Grundstil för alla popups
  popups.querySelectorAll(":scope > *").forEach((el) => {
    el.removeAttribute("transform");
    el.removeAttribute("visibility");
    el.style.transformOrigin = "center";
    el.style.transformBox = "fill-box";
    el.style.transform = "scale(0.1)";
    el.style.opacity = "0";
    el.style.visibility = "hidden";


    if(aniPop) {
      el.style.transition =
        "transform 0.4s cubic-bezier(0.25,1,0.5,1), opacity 0.3s ease, visibility 0s linear 0.3s";
    }

  });

  if (startmess) {
    startmess.removeAttribute("visibility");
    startmess.style.visibility = "visible";
    startmess.style.opacity = "1";
    startmess.style.transform = "scale(1)";
  }

  let activePopup = null; // håller reda på aktiv popup

  // 🎯 Klicklogik för targets
  targets.querySelectorAll("*[id^='target']").forEach((target) => {
    target.style.cursor = "pointer";
    target.style.pointerEvents = "all";

    target.addEventListener("click", () => {
      const id = target.id.replace("target", "");
      const popup =
        popups.querySelector(`#popup${id}`) ||
        [...popups.querySelectorAll("*")].find((el) =>
          el.id.toLowerCase().includes(id.toLowerCase())
        );

    /*
      // 🧠 Toggle: stäng om den redan är aktiv
      if (activePopup === popup) {
        popup.style.transform = "scale(0.1)";
        popup.style.opacity = "0";
        popup.style.visibility = "hidden";
        activePopup = null;
        return;
      }
        */

      // 🔻 Dölj alla popups
      popups.querySelectorAll(":scope > *").forEach((el) => {
        el.style.transform = "scale(0.1)";
        el.style.opacity = "0";
        el.style.visibility = "hidden";
      });

      if (startmess) {
        startmess.style.transform = "scale(0.1)";
        startmess.style.opacity = "0";
        startmess.style.visibility = "hidden";
      }

      // 🔺 Visa popup som hör till target
      if (popup) {

        if(aniPop) {

          popup.style.transition =
            "transform 0.3s cubic-bezier(0.25,1.25,0.5,1.1), opacity 0.3s ease";
          popup.style.visibility = "visible";
          popup.style.opacity = "1";
          popup.style.transform = "scale(1.1)";
          setTimeout(() => {
            popup.style.transform = "scale(1)";
          }, 250);

        } else {

          popup.style.visibility = "visible";
          popup.style.opacity = "1";
          popup.style.transform = "scale(1)";

        }

        activePopup = popup;

        // 💥 Lägg till klick på popup för att stänga den
        popup.style.cursor = "pointer";
        /*
        popup.addEventListener(
          "click",
          (e) => {
            e.stopPropagation(); // hindra att event bubblar upp
            popup.style.transform = "scale(0.1)";
            popup.style.opacity = "0";
            popup.style.visibility = "hidden";
            activePopup = null;
          },
          { once: true } // lyssna bara en gång (förnyas nästa gång popup öppnas)
        );
*/
      }
    });
  });

  svg.dataset.interactiveReady = "true";
});
});



function findLayer(svg, name) {
  // försök först vanliga selektorer
  let layer =
    svg.querySelector(`[id='${name}'], [data-name='${name}'], [inkscape\\:label='${name}']`);
  if (layer) return layer;

  // 🧩 Safari fallback: loopa manuellt genom alla element
  const all = svg.getElementsByTagName("*");
  for (const el of all) {
    const id = el.getAttribute("id");
    const dataName = el.getAttribute("data-name");
    const label = el.getAttribute("inkscape:label");
    if (id === name || dataName === name || label === name) return el;
  }
  return null;
}

function applyRenderingAttributes(svg) {
  if (!svg || svg.nodeName !== "svg") return;

  // 💎 precision & textkvalitet
  if (!svg.hasAttribute("shape-rendering")) {
    svg.setAttribute("shape-rendering", "geometricPrecision");
  }
  if (!svg.hasAttribute("text-rendering")) {
    svg.setAttribute("text-rendering", "geometricPrecision");
  }

  // 🎨 färghantering
  if (!svg.hasAttribute("color-interpolation")) {
    svg.setAttribute("color-interpolation", "sRGB");
  }
  if (!svg.hasAttribute("color-rendering")) {
    svg.setAttribute("color-rendering", "optimizeQuality");
  }

  // 🧮 säkerställ att tunna linjer inte skalar upp
  svg.querySelectorAll("[stroke]").forEach((el) => {
    if (!el.hasAttribute("vector-effect")) {
      el.setAttribute("vector-effect", "non-scaling-stroke");
    }
  });

  // 🧠 font-rendering och aliasing (via CSS inline)
  svg.style.webkitFontSmoothing = "antialiased";
  svg.style.MozOsxFontSmoothing = "grayscale";
  svg.style.textRendering = "geometricPrecision";
}


</script>

{#if show}
<div class="svgWrapper {className}" style={style} bind:this={wrapper}>
  {@html svgMarkup}
</div>
{/if}

<style>
  .svgWrapper {
    position: relative;
    width: 100%;
  }

  svg {
    max-width: min(100%, 1800px);
    height: auto;
  }
</style>